import { Link } from 'react-router-dom';
import { Star, TrendingUp, Check } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { optimizeImageUrl } from '@/lib/imageUtils';

interface ComparisonProduct {
  slug: string;
  title: string;
  image?: string;
  price?: number;
  msrp?: number;
  price_duration?: string;
  rating?: number;
  review_count?: number;
  topFeatures: string[];
  compatibilityScore?: number;
  service_type?: string;
  instructor_info?: {
    name?: string;
  };
  total_video_hours?: number;
  certificate_included?: boolean;
  student_count?: number;
  skill_level?: string;
}

interface ChatComparisonGridProps {
  products: ComparisonProduct[];
  winner?: string; // slug of winning product
  winnerReason?: string;
}

export function ChatComparisonGrid({ products, winner, winnerReason }: ChatComparisonGridProps) {
  const isCourseComparison = products.some(p => p.service_type === 'education');

  return (
    <div className="space-y-3">
      {/* Winner Callout */}
      {winner && winnerReason && (
        <Card className="border-primary bg-primary/5">
          <CardContent className="p-3">
            <div className="flex items-start gap-2">
              <TrendingUp className="w-4 h-4 text-primary shrink-0 mt-0.5" />
              <div>
                <p className="text-sm font-semibold text-foreground">Best Match</p>
                <p className="text-xs text-muted-foreground mt-0.5">{winnerReason}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Comparison Grid */}
      <div className="grid gap-3 md:grid-cols-2 lg:grid-cols-3">
        {products.map((product) => {
          const isWinner = product.slug === winner;
          const isCourse = product.service_type === 'education';
          const displayPrice = product.price || 0;
          const formattedPrice = displayPrice === 0 ? 'Free' : `$${displayPrice}`;
          const duration = product.price_duration === 'mo' ? '/mo' : 
                          product.price_duration === 'yr' ? '/yr' : 
                          product.price_duration === 'one-time' ? '' : '';
          const discountPercent = product.msrp && product.msrp > displayPrice 
            ? Math.round(((product.msrp - displayPrice) / product.msrp) * 100) 
            : 0;

          return (
            <Card
              key={product.slug}
              className={`overflow-hidden transition-all hover:shadow-md ${
                isWinner ? 'ring-2 ring-primary' : ''
              }`}
            >
              <CardHeader className="p-3 pb-2">
                <div className="flex items-start gap-3">
                  {/* Product Image */}
                  <div className="w-12 h-12 rounded-md overflow-hidden bg-muted shrink-0">
                    {product.image ? (
                      <img
                        src={optimizeImageUrl(product.image, { width: 48, height: 48 })}
                        alt={product.title}
                        className="w-full h-full object-cover"
                        loading="lazy"
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center">
                        <span className="text-xl">📦</span>
                      </div>
                    )}
                  </div>

                  <div className="flex-1 min-w-0">
                    <CardTitle className="text-sm leading-tight line-clamp-2">
                      {product.title}
                    </CardTitle>
                    {isWinner && (
                      <Badge variant="default" className="text-xs mt-1">
                        Recommended
                      </Badge>
                    )}
                  </div>
                </div>
              </CardHeader>

              <CardContent className="p-3 pt-0 space-y-2">
                {/* Price & Rating */}
                <div className="flex items-center justify-between">
                  <div>
                    <div className="flex items-baseline gap-1">
                      <span className="text-base font-bold text-foreground">
                        {formattedPrice}
                      </span>
                      {duration && (
                        <span className="text-xs text-muted-foreground">{duration}</span>
                      )}
                    </div>
                    {discountPercent > 0 && (
                      <div className="flex items-center gap-1 mt-0.5">
                        <span className="text-xs text-muted-foreground line-through">
                          ${product.msrp}
                        </span>
                        <span className="text-xs text-course-accent font-bold">
                          {discountPercent}% OFF
                        </span>
                      </div>
                    )}
                  </div>

                  {product.rating && product.rating > 0 && (
                    <div className="flex items-center gap-1">
                      <Star className="w-3 h-3 fill-yellow-400 text-yellow-400" />
                      <span className="text-xs font-medium">
                        {product.rating.toFixed(1)}
                      </span>
                      {product.review_count && (
                        <span className="text-xs text-muted-foreground">
                          ({product.review_count})
                        </span>
                      )}
                    </div>
                  )}
                </div>
                
                {/* Course-Specific Info */}
                {isCourse && product.instructor_info?.name && (
                  <div className="text-xs text-muted-foreground">
                    by <span className="font-semibold text-foreground">{product.instructor_info.name}</span>
                  </div>
                )}
                
                {isCourse && product.skill_level && (
                  <Badge variant="secondary" className="text-xs w-fit">
                    {product.skill_level}
                  </Badge>
                )}

                {/* Compatibility Score */}
                {product.compatibilityScore !== undefined && (
                  <div className="flex items-center gap-2">
                    <div className="flex-1 h-1.5 bg-muted rounded-full overflow-hidden">
                      <div
                        className="h-full bg-primary rounded-full transition-all"
                        style={{ width: `${product.compatibilityScore}%` }}
                      />
                    </div>
                    <span className="text-xs font-medium text-muted-foreground">
                      {product.compatibilityScore}% match
                    </span>
                  </div>
                )}

                {/* Top Features */}
                <div className="space-y-1">
                  {product.topFeatures.slice(0, 3).map((feature, idx) => (
                    <div key={idx} className="flex items-start gap-1.5">
                      <Check className="w-3 h-3 text-primary shrink-0 mt-0.5" />
                      <span className="text-xs text-muted-foreground line-clamp-1">
                        {feature}
                      </span>
                    </div>
                  ))}
                </div>

                {/* View Details Button */}
                <Link to={`/product/${product.slug}`}>
                  <Button size="sm" variant="outline" className="w-full mt-2 h-8 text-xs">
                    {isCourse ? 'View Course' : 'View Details'}
                  </Button>
                </Link>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Full Comparison Link */}
      <div className="text-center">
        <Link to={`/compare?ids=${products.map(p => p.slug).join(',')}`}>
          <Button variant="outline" size="sm" className="text-xs">
            View Full Side-by-Side Comparison
          </Button>
        </Link>
      </div>
    </div>
  );
}
